import React, { useCallback, useMemo, useEffect, useRef } from "react";

/** Utilitário simples para classes condicional (clsx) */
function clsx(...args: (string | false | undefined | null)[]) {
  return args.filter(Boolean).join(" ");
}

/**
 * Opção individual para o RadioButton.
 */
interface RadioButtonOption {
  /** Texto exibido ao usuário */
  label: string;
  /** Valor associado à opção */
  value: string;
  /** Descrição opcional para acessibilidade ou contexto extra */
  description?: string;
}

/**
 * Propriedades do componente RadioButton.
 */
interface RadioButtonProps {
  /** Lista de opções a serem exibidas */
  options: RadioButtonOption[];
  /** Valor atualmente selecionado */
  selectedValue: string;
  /** Callback chamado ao selecionar uma opção */
  onValueChange: (value: string) => void;
  /** Rótulo do grupo de radio (opcional) */
  label?: string;
  /** Descrição global do grupo (opcional) */
  description?: string;
  /** Desabilita todas as opções */
  disabled?: boolean;
  /** Tema visual: 'light' (padrão) ou 'dark' */
  theme?: "light" | "dark";
  /** Nome do grupo (útil para formulários) */
  name?: string;
  /** Torna a seleção obrigatória */
  required?: boolean;
  /** Texto de ajuda exibido abaixo do grupo */
  helperText?: string;
  /** Data-testid para testes automatizados */
  "data-testid"?: string;
  /** Indica se há erro de validação */
  error?: boolean;
  /** Mensagem de erro (opcional) */
  errorMessage?: string;
  /** Tamanho do radio: 'sm' | 'md' | 'lg' (padrão: 'md') */
  size?: "sm" | "md" | "lg";
  /** Classe de cor primária customizada (ex: 'blue', 'emerald', 'rose') */
  primaryColor?: "blue" | "emerald";
  /** Valor inicial para uso não-controlado */
  defaultValue?: string;
  /** Se true, foca automaticamente o radio selecionado ao montar */
  autoFocus?: boolean;
  /** Função chamada ao perder o foco */
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  /** Função chamada ao ganhar o foco */
  onFocus?: React.FocusEventHandler<HTMLInputElement>;
  /** Se quiser aceitar qualquer prop extra para o input */
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
}

/**
 * Componente RadioButton acessível e estilizado com Tailwind, inspirado no Fluent UI.
 *
 * Recursos:
 * - Suporte a tamanhos (`size`: sm, md, lg) e cor primária customizável (`primaryColor`).
 * - Usa <fieldset> e <legend> para agrupamento semântico e acessibilidade nativa.
 * - Usa aria-describedby para associar helperText, descrição global e mensagens de erro ao grupo.
 * - Cada input recebe id único e label associado via htmlFor.
 * - Suporte a tema claro/escuro.
 * - Destaque visual de foco e feedback de erro (borda/vermelho).
 * - Navegação por teclado aprimorada (setas e tab).
 * - Memoização para evitar renders desnecessários.
 * - Suporte a modo controlado e não-controlado (`selectedValue` e `defaultValue`).
 * - Foco automático opcional ao montar (`autoFocus`).
 * - Suporte a integração com libs de formulário via `onBlur`, `onFocus` e `inputProps`.
 * - Testabilidade: cada input e label recebe `data-testid` único.
 *
 * @example
 * ```tsx
 * <RadioButton
 *   label="Forma de pagamento"
 *   description="Escolha como deseja pagar"
 *   options={[
 *     { label: "Cartão de Crédito", value: "credit" },
 *     { label: "Pix", value: "pix", description: "Pagamento instantâneo" },
 *     { label: "Boleto", value: "boleto" },
 *   ]}
 *   selectedValue={selected}
 *   onValueChange={setSelected}
 *   theme="dark"
 *   size="lg"
 *   primaryColor="emerald"
 *   required
 *   helperText="Escolha uma opção para continuar"
 *   error={false}
 *   autoFocus
 *   onBlur={...}
 *   onFocus={...}
 *   inputProps={{ ... }}
 * />
 * ```
 */
const RadioButton: React.FC<RadioButtonProps> = React.memo(({
  options,
  selectedValue,
  onValueChange,
  defaultValue,
  label,
  description,
  disabled = false,
  theme = "light",
  name,
  required = false,
  helperText,
  "data-testid": dataTestId,
  error = false,
  errorMessage,
  size = "md",
  primaryColor = "blue",
  autoFocus = false,
  onBlur,
  onFocus,
  inputProps,
}) => {
  const isDark = theme === "dark";
  // Gere os IDs apenas uma vez por instância do componente, usando useId para SSR/hydration seguro
  const groupId = React.useId();
  const groupName = name || groupId;

  const helperId = helperText ? `${groupId}-helper` : undefined;
  const errorId = error && errorMessage ? `${groupId}-error` : undefined;
  const descId = description ? `${groupId}-desc` : undefined;
  const describedBy = [helperId, errorId, descId].filter(Boolean).join(" ") || undefined;

  const [internalValue, setInternalValue] = React.useState(defaultValue ?? "");
  const isControlled = selectedValue !== undefined;
  const value = isControlled ? selectedValue : internalValue;

  const [focusedIdx, setFocusedIdx] = React.useState<number | null>(null);

  const sizeMap = {
    sm: {
      label: "min-w-[80px] px-2 py-1 text-sm",
      radio: "w-4 h-4 mr-2",
      dot: "w-2 h-2",
    },
    md: {
      label: "min-w-[120px] px-3 py-2 text-base",
      radio: "w-5 h-5 mr-3",
      dot: "w-2.5 h-2.5",
    },
    lg: {
      label: "min-w-[160px] px-4 py-3 text-lg",
      radio: "w-6 h-6 mr-4",
      dot: "w-3 h-3",
    },
  }[size];

  const colorMap: Record<"blue" | "emerald", {
    checked: string;
    checkedDark: string;
    unchecked: string;
    uncheckedDark: string;
    labelChecked: string;
    labelCheckedDark: string;
    labelUnchecked: string;
    labelUncheckedDark: string;
    focus: string;
  }> = {
    blue: {
      checked: "bg-blue-600 border-blue-600",
      checkedDark: "bg-blue-500 border-blue-400",
      unchecked: "bg-white border-gray-400",
      uncheckedDark: "bg-gray-900 border-gray-500",
      labelChecked: "bg-blue-50 border-blue-600",
      labelCheckedDark: "bg-blue-900/40 border-blue-400",
      labelUnchecked: "bg-white border-gray-300 hover:border-blue-600",
      labelUncheckedDark: "bg-gray-800 border-gray-600 hover:border-blue-400",
      focus: "ring-2 ring-blue-500 ring-offset-2",
    },
    emerald: {
      checked: "bg-emerald-600 border-emerald-600",
      checkedDark: "bg-emerald-500 border-emerald-400",
      unchecked: "bg-white border-gray-400",
      uncheckedDark: "bg-gray-900 border-gray-500",
      labelChecked: "bg-emerald-50 border-emerald-600",
      labelCheckedDark: "bg-emerald-900/40 border-emerald-400",
      labelUnchecked: "bg-white border-gray-300 hover:border-emerald-600",
      labelUncheckedDark: "bg-gray-800 border-gray-600 hover:border-emerald-400",
      focus: "ring-2 ring-emerald-500 ring-offset-2",
    },
    // ...adicione outras cores se necessário
  };

  const colorSet = colorMap[primaryColor] || colorMap.blue;

  const colorChecked = isDark ? colorSet.checkedDark : colorSet.checked;
  const colorUnchecked = isDark ? colorSet.uncheckedDark : colorSet.unchecked;
  const colorLabelChecked = isDark ? colorSet.labelCheckedDark : colorSet.labelChecked;
  const colorLabelUnchecked = isDark ? colorSet.labelUncheckedDark : colorSet.labelUnchecked;
  const colorFocus = colorSet.focus;

  const fieldsetClass = useMemo(() =>
    clsx(
      "w-full mb-4 border-0 p-0 m-0",
      disabled && "opacity-60 pointer-events-none"
    ),
    [disabled]
  );

  const legendClass = useMemo(() =>
    clsx(
      "text-base font-semibold mb-2",
      isDark ? "text-gray-200" : "text-gray-800"
    ),
    [isDark]
  );

  const groupDivClass = "flex flex-wrap gap-4";

  const handleChange = useCallback(
    (val: string) => {
      if (!disabled && val !== value) {
        if (isControlled) {
          onValueChange(val);
        } else {
          setInternalValue(val);
          onValueChange?.(val);
        }
      }
    },
    [disabled, value, isControlled, onValueChange]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLFieldSetElement>) => {
      if (disabled) return;
      const idx = focusedIdx ?? options.findIndex(o => o.value === selectedValue);
      if (e.key === "ArrowRight" || e.key === "ArrowDown") {
        e.preventDefault();
        const next = (idx + 1) % options.length;
        handleChange(options[next].value);
        setFocusedIdx(next);
      } else if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
        e.preventDefault();
        const prev = (idx - 1 + options.length) % options.length;
        handleChange(options[prev].value);
        setFocusedIdx(prev);
      }
    },
    [disabled, focusedIdx, options, selectedValue, handleChange]
  );

  // Refs para inputs para foco programático
  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);
  // Para a maioria dos casos, não é necessário memoizar o array de refs.
  // Se as opções mudam muito dinamicamente, pode-se usar useMemo assim:
  // const inputRefs = useMemo(() => Array(options.length).fill(null).map(() => React.createRef<HTMLInputElement>()), [options.length]);

  // Foco automático apenas no mount (ou se autoFocus for true)
  useEffect(() => {
    if (!autoFocus) return;
    const idx = options.findIndex(o => o.value === value);
    if (
      idx >= 0 &&
      inputRefs.current[idx] &&
      typeof document !== "undefined" &&
      document.activeElement?.tagName === "BODY"
    ) {
      inputRefs.current[idx]?.focus();
      setFocusedIdx(idx);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoFocus]); // Só no mount ou se autoFocus mudar

  return (
    <fieldset
      className={fieldsetClass}
      aria-disabled={disabled}
      aria-describedby={describedBy}
      aria-invalid={error ? "true" : undefined}
      data-testid={dataTestId || "radio-group"}
      tabIndex={-1}
      onKeyDown={handleKeyDown}
    >
      {label && (
        <legend className={legendClass}>
          {label}
          {required && <span className="text-red-600 ml-1">*</span>}
        </legend>
      )}
      {description && (
        <p
          id={descId}
          className={clsx("mb-2 text-sm", isDark ? "text-gray-400" : "text-gray-600")}
        >
          {description}
        </p>
      )}
      {/* Removido role="radiogroup" pois fieldset já cobre acessibilidade */}
      <div className={groupDivClass}>
        {options.map((option, idx) => {
          const checked = value === option.value;
          const inputId = `${groupId}-option-${idx}`;
          const labelClass = clsx(
            "flex items-center cursor-pointer rounded-lg transition border-2 shadow-sm outline-none",
            sizeMap.label,
            checked ? colorLabelChecked : colorLabelUnchecked,
            focusedIdx === idx && colorFocus,
            error && checked && "border-red-600 ring-2 ring-red-500"
          );
          const radioVisualClass = clsx(
            "flex items-center justify-center rounded-full border-2 transition relative",
            sizeMap.radio,
            checked ? colorChecked : colorUnchecked
          );
          const dotClass = clsx(
            "block rounded-full",
            sizeMap.dot,
            isDark ? "bg-gray-900" : "bg-white"
          );

          return (
            <div key={option.value} className="flex flex-col">
              <input
                ref={el => { inputRefs.current[idx] = el; }}
                type="radio"
                id={inputId}
                name={groupName}
                value={option.value}
                checked={checked}
                onChange={() => handleChange(option.value)}
                disabled={disabled}
                className="sr-only"
                aria-label={option.label}
                required={required}
                aria-describedby={option.description ? `${inputId}-desc` : undefined}
                tabIndex={checked ? 0 : -1}
                onFocus={e => {
                  setFocusedIdx(idx);
                  onFocus?.(e);
                }}
                onBlur={e => {
                  setFocusedIdx(null);
                  onBlur?.(e);
                }}
                data-testid={`radio-input-${option.value}`}
                {...inputProps}
              />
              <label
                htmlFor={inputId}
                className={labelClass}
                tabIndex={-1}
                aria-checked={checked}
                aria-disabled={disabled}
                role="radio"
                data-testid={`radio-label-${option.value}`}
              >
                <span className={radioVisualClass} aria-hidden="true">
                  {checked && <span className={dotClass} />}
                </span>
                <span
                  className={clsx(
                    "select-none",
                    isDark ? "text-gray-100" : "text-gray-900"
                  )}
                >
                  {option.label}
                </span>
                {option.description && (
                  <span
                    id={`${inputId}-desc`}
                    className={clsx(
                      "ml-2 text-xs",
                      isDark ? "text-gray-400" : "text-gray-500"
                    )}
                  >
                    {option.description}
                  </span>
                )}
              </label>
            </div>
          );
        })}
      </div>
      {helperText && (
        <p
          className={clsx("mt-2 text-sm", isDark ? "text-gray-400" : "text-gray-600")}
          id={helperId}
          aria-live="polite"
        >
          {helperText}
        </p>
      )}
      {error && errorMessage && (
        <p
          className="mt-2 text-sm text-red-600"
          id={errorId}
          aria-live="assertive"
        >
          {errorMessage}
        </p>
      )}
    </fieldset>
  );
});

RadioButton.displayName = "RadioButton";
export default RadioButton;
export type { RadioButtonProps };